import React, { useState, useEffect, useMemo } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { runInventoryTransaction } from '@/lib/api/inventoryTransactions';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QRCodeSVG } from 'qrcode.react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertCircle, X, Check, AlertTriangle, RotateCcw, Printer, ChevronUp, ChevronDown, Truck, QrCode } from "lucide-react";
import { ColorBadge } from "@/components/ColorBadge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useColorSettings } from '@/contexts/ColorSettingsContext';
import { printPreparedOrderDocument } from "@/lib/preparedOrderPrint";

export function PrepareOrderForm({
  orderId,
  onClose,
  onUpdated,
  onServe,
  mobilePicking = false,
  showMobileQr = true,
}) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [quantityTouched, setQuantityTouched] = useState({});
  const [notes, setNotes] = useState({});
  const [error, setError] = useState(null);
  const [stockAvailable, setStockAvailable] = useState({});
  const [showOnlyPrepared, setShowOnlyPrepared] = useState(false);
  const [showSummary, setShowSummary] = useState(!mobilePicking);
  const [codeFilter, setCodeFilter] = useState('');
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [showPrintAfterSaveDialog, setShowPrintAfterSaveDialog] = useState(false);
  const [isPreparedDocumentPrinting, setIsPreparedDocumentPrinting] = useState(false);
  const { toast } = useToast();
  const { colorSettings } = useColorSettings();

  const mobilePickingUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}${window.location.pathname}${window.location.search}#/preparar-pedido/${orderId}`;
  }, [orderId]);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setError(null);
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          number,
          date,
          status,
          preparation_status,
          total,
          customer:customers(
            id,
            name
          ),
          order_items(
            id,
            quantity,
            pending_quantity,
            delivered_quantity,
            preparation_quantity,
            preparation_status,
            price,
            product:products(
              id,
              code,
              description,
              images,
              main_image_index,
              inventory(
                id,
                quantity,
                size,
                color
              )
            ),
            size,
            color
          )
        `)
        .eq('id', orderId)
        .single();

      if (error) throw error;

      // Inicializar las cantidades a preparar con 0
      const initialQuantities = {};
      const initialTouched = {};
      const initialNotes = {};
      const initialStock = {};
      
      data.order_items.forEach(item => {
        initialQuantities[item.id] = 0; // Siempre inicializar en 0
        initialTouched[item.id] = false;
        initialNotes[item.id] = '';
        
        // Calcular stock disponible
        const inventoryItem = item.product.inventory.find(
          inv => inv.size === item.size && inv.color === item.color
        );
        initialStock[item.id] = inventoryItem?.quantity || 0;
      });
      
      setQuantities(initialQuantities);
      setQuantityTouched(initialTouched);
      setNotes(initialNotes);
      setStockAvailable(initialStock);
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setError("No se pudieron cargar los detalles del pedido");
      toast({
        title: "Error",
        description: "No se pudieron cargar los detalles del pedido",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetInputs = () => {
    // Poner a cero todas las cantidades y limpiar notas sin tocar la BD
    const resetQuantities = {};
    const resetTouched = {};
    const resetNotes = {};
    (order?.order_items || []).forEach(item => {
      resetQuantities[item.id] = 0;
      resetTouched[item.id] = false;
      resetNotes[item.id] = '';
    });
    setQuantities(resetQuantities);
    setQuantityTouched(resetTouched);
    setNotes(resetNotes);
    toast({ title: "Campos reiniciados", description: "Se han limpiado las cantidades y notas a preparar" });
  };

  const handleResetPreparedAll = async () => {
    try {
      if (!order) return;
      const itemsWithPreparation = order.order_items.filter(i => (i.preparation_quantity || 0) > 0);
      if (itemsWithPreparation.length === 0) {
        toast({ title: "Nada que resetear", description: "No hay líneas ya preparadas" });
        return;
      }

      if (!window.confirm("¿Seguro que quieres resetear TODAS las cantidades ya preparadas?")) {
        return;
      }

      setPreparing(true);
      setError(null);

      await persistPreparation(itemsWithPreparation, () => 0, 'Reseteo masivo de preparación');

      // Limpiar los campos tras recargar en persistPreparation.
      handleResetInputs();

      toast({ title: "Preparación reseteada", description: "Se ha eliminado la preparación de todas las líneas" });
      if (onUpdated) onUpdated();
    } catch (e) {
      console.error('Error resetting prepared all:', e);
      setError(e.message || 'No se pudo resetear la preparación');
      toast({ title: 'Error', description: e.message || 'No se pudo resetear la preparación', variant: 'destructive' });
    } finally {
      setPreparing(false);
    }
  };

  const handleQuantityChange = (itemId, value) => {
    const item = order.order_items.find(i => i.id === itemId);
    const pendingQty = item.pending_quantity;
    const newValue = Math.min(Math.max(0, parseInt(value) || 0), pendingQty);

    setQuantities(prev => ({
      ...prev,
      [itemId]: newValue
    }));
    setQuantityTouched(prev => ({
      ...prev,
      [itemId]: true
    }));
  };

  const handlePrepareAll = () => {
    if (!window.confirm("Se prepararán automáticamente todas las cantidades cubiertas por stock teórico. Las tallas sin stock quedarán pendientes. ¿Continuar?")) {
      return;
    }
    const newQuantities = {};
    const newTouched = {};
    order.order_items.forEach(item => {
      // Calcular la cantidad realmente pendiente (cantidad total pendiente - cantidad ya preparada)
      const realPendingQuantity = Math.max(0, item.pending_quantity - item.preparation_quantity);
      
      // Solo procesar si hay cantidad realmente pendiente
      if (realPendingQuantity > 0) {
        const stockQty = stockAvailable[item.id] || 0;
        // Solo establecer la cantidad si hay stock disponible
        if (stockQty > 0) {
          newQuantities[item.id] = Math.min(item.pending_quantity, (item.preparation_quantity || 0) + Math.min(realPendingQuantity, stockQty));
          newTouched[item.id] = true;
        }
      }
    });
    setQuantities(prev => ({ ...prev, ...newQuantities }));
    setQuantityTouched(prev => ({ ...prev, ...newTouched }));
  };

  const handlePrepareItem = (itemId) => {
    const item = order.order_items.find(i => i.id === itemId);
    if (item && item.pending_quantity > 0) {
      const stockQty = stockAvailable[itemId] || 0;
      const realPendingQuantity = Math.max(0, item.pending_quantity - (item.preparation_quantity || 0));
      setQuantities(prev => ({
        ...prev,
        [itemId]: Math.min(item.pending_quantity, (item.preparation_quantity || 0) + Math.min(realPendingQuantity, stockQty))
      }));
      setQuantityTouched(prev => ({
        ...prev,
        [itemId]: true
      }));
    }
  };

  const handleNoteChange = (itemId, value) => {
    setNotes(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  const getTargetPreparationQuantity = (item, quantityMap = quantities, touchedMap = quantityTouched) => (
    touchedMap[item.id] ? (quantityMap[item.id] || 0) : (item.preparation_quantity || 0)
  );

  const persistPreparation = async (items, quantityForItem, note = null) => {
    const payload = items.map(item => ({
      id: item.id,
      quantity: Number(quantityForItem(item)),
      expected_delivered: Number(item.delivered_quantity) || 0,
      expected_pending: Number(item.pending_quantity) || 0,
      expected_prepared: Number(item.preparation_quantity) || 0,
      notes: note ?? notes[item.id] ?? '',
    }));
    const result = await runInventoryTransaction('prepare_order_transaction', orderId, {
      p_order_id: orderId, p_items: payload,
    });
    await fetchOrderDetails();
    setQuantityTouched({});
    if (result.recoveredPreviousOperation) {
      toast({ title: 'Preparación anterior confirmada', description: 'Revise las cantidades actualizadas antes de continuar.' });
    }
    if (onUpdated) onUpdated();
    return result;
  };

  const savePreparationForItems = async (items, quantityMap, touchedMap, successDescription) => {
    const changed = items.filter(item => getTargetPreparationQuantity(item, quantityMap, touchedMap) !== (item.preparation_quantity || 0));
    if (!changed.length) {
      toast({ title: 'Sin cambios', description: 'No hay modificaciones para guardar' });
      return false;
    }
    const result = await persistPreparation(changed, item => getTargetPreparationQuantity(item, quantityMap, touchedMap));
    if (result.recoveredPreviousOperation) return false;
    toast({ title: 'Éxito', description: successDescription });
    return true;
  };

  const handleUnprepare = async (itemId) => {
    try {
      const item = order.order_items.find(i => i.id === itemId);
      if (!item || !item.preparation_quantity) return;

      setPreparing(true);
      setError(null);

      await persistPreparation([item], () => 0, 'Preparación cancelada');

      toast({
        title: "Éxito",
        description: "La línea se ha despreparado correctamente",
      });
    } catch (error) {
      console.error('Error unpreparing item:', error);
      setError(error.message || "No se pudo despreparar la línea");
      toast({
        title: "Error",
        description: error.message || "No se pudo despreparar la línea",
        variant: "destructive"
      });
    } finally {
      setPreparing(false);
    }
  };

  const handlePrepare = async () => {
    try {
      setError(null);
      setPreparing(true);

      const saved = await savePreparationForItems(order.order_items, quantities, quantityTouched, 'El pedido se ha preparado correctamente');
      if (!saved) return;
      if (mobilePicking) {
        setShowPrintAfterSaveDialog(true);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error preparing order:', error);
      setError(error.message || "No se pudo preparar el pedido");
      toast({
        title: "Error",
        description: error.message || "No se pudo preparar el pedido",
        variant: "destructive"
      });
    } finally {
      setPreparing(false);
    }
  };

  // Comparador de tallas: orden P, M, L, XL, 2XL, 3XL, 4XL, 5XL; soporta sinónimos (S≈P, XXL≈2XL, XXXL≈3XL) y numéricas
  const compareSizeStrings = (a, b) => {
    const normalize = (s) => (s || '').toString().trim().toLowerCase();
    const na = normalize(a);
    const nb = normalize(b);

    const rank = {
      'p': 1,
      's': 1,
      'm': 2,
      'l': 3,
      'xl': 4,
      '2xl': 5,
      'xxl': 5,
      '3xl': 6,
      'xxxl': 6,
      '4xl': 7,
      '5xl': 8
    };

    const ra = rank[na];
    const rb = rank[nb];
    if (ra !== undefined && rb !== undefined) return ra - rb;

    const extractFirstInt = (str) => {
      const m = (str || '').match(/\d+/);
      return m ? parseInt(m[0], 10) : null;
    };
    const ia = extractFirstInt(na);
    const ib = extractFirstInt(nb);
    if (ia !== null && ib !== null) return ia - ib;

    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    if (ia !== null) return -1;
    if (ib !== null) return 1;

    return na.localeCompare(nb, 'es', { numeric: true, sensitivity: 'base' });
  };

  const compareColorStrings = (a, b) => (
    (a || '').toString().localeCompare((b || '').toString(), 'es', { numeric: true, sensitivity: 'base' })
  );

  const groupItemsByProduct = (groupBy = 'color') => {
    if (!order) return [];
    
    const groupedItems = {};
    order.order_items.forEach(item => {
      if (showOnlyPrepared && (item.preparation_quantity || 0) === 0) return;

      const groupValue = groupBy === 'size' ? item.size : item.color;
      const productKey = item.product.id || item.product.code;
      const key = `${productKey}-${groupBy}-${groupValue}`;
      if (!groupedItems[key]) {
        groupedItems[key] = {
          key,
          productId: item.product.id,
          productCode: item.product.code,
          productDescription: item.product.description,
          color: groupBy === 'color' ? item.color : null,
          size: groupBy === 'size' ? item.size : null,
          productImage: item.product.images?.[item.product.main_image_index || 0],
          items: []
        };
        

      }
      groupedItems[key].items.push(item);
    });
    
    // Construir lista de grupos con items
    let groups = Object.values(groupedItems).filter(group => group.items.length > 0);

    // Filtro por código de producto
    if (codeFilter && codeFilter.trim() !== '') {
      const q = codeFilter.trim().toLowerCase();
      groups = groups.filter(g => (g.productCode || '').toLowerCase().includes(q));
    }

    // Orden por código de producto y, en empate, por color o talla según la vista
    groups.sort((a, b) => {
      const byCode = (a.productCode || '').localeCompare(b.productCode || '', 'es', { numeric: true, sensitivity: 'base' });
      if (byCode !== 0) return byCode;
      if (groupBy === 'size') return compareSizeStrings(a.size, b.size);
      return (a.color || '').localeCompare(b.color || '', 'es', { numeric: true, sensitivity: 'base' });
    });

    // Ordenar tallas o colores dentro de cada grupo según la vista
    groups.forEach(g => {
      g.items.sort((i1, i2) => (
        groupBy === 'size'
          ? compareColorStrings(i1.color, i2.color)
          : compareSizeStrings(i1.size, i2.size)
      ));
    });

    return groups;
  };

  const getVisibleSizes = (groups) => {
    const sizes = new Set();
    groups.forEach(group => {
      group.items.forEach(item => sizes.add(item.size));
    });
    return Array.from(sizes).sort(compareSizeStrings);
  };

  const getCellData = (item) => {
    const stockQty = stockAvailable[item.id] || 0;
    const savedPreparedQty = item.preparation_quantity || 0;
    const effectivePreparedQty = getTargetPreparationQuantity(item);
    const remainingToPrepare = Math.max(0, (item.pending_quantity || 0) - effectivePreparedQty);
    const hasDraft = !!quantityTouched[item.id] && effectivePreparedQty > 0;

    let state = 'empty';
    if ((item.pending_quantity || 0) === 0 || remainingToPrepare === 0) {
      state = effectivePreparedQty > 0 || (item.delivered_quantity || 0) > 0 ? 'prepared' : 'empty';
    } else if (hasDraft && effectivePreparedQty > savedPreparedQty) {
      state = 'prepared';
    } else if (stockQty >= remainingToPrepare) {
      state = 'stock';
    } else {
      state = 'no-stock';
    }

    return {
      stockQty,
      savedPreparedQty,
      effectivePreparedQty,
      remainingToPrepare,
      displayQty: state === 'prepared' ? Math.max(0, effectivePreparedQty - savedPreparedQty) || effectivePreparedQty : remainingToPrepare,
      state,
      hasDraft
    };
  };

  const getCellClassName = (state) => {
    if (state === 'prepared') return 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200';
    if (state === 'stock') return 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200';
    if (state === 'no-stock') return 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200';
    return 'bg-gray-50 text-gray-300 border-gray-200';
  };

  const handleCellClick = (item) => {
    const cell = getCellData(item);
    if (cell.state === 'stock') {
      handleQuantityChange(item.id, item.pending_quantity || 0);
      return;
    }
    if (cell.state === 'no-stock') {
      setQuantities(prev => ({ ...prev, [item.id]: item.preparation_quantity || 0 }));
      setQuantityTouched(prev => ({ ...prev, [item.id]: false }));
      toast({
        title: 'Sin stock teórico',
        description: 'Se propone 0. Regulariza stock con entrada de almacén antes de servir.',
      });
      return;
    }
    if (cell.state === 'prepared') {
      toast({ title: 'Cantidad validada', description: 'Usa el detalle para modificarla.' });
    }
  };

  const openQuantityEditor = (item) => {
    const cell = getCellData(item);
    const defaultValue = cell.state === 'no-stock' ? 0 : cell.effectivePreparedQty;
    const response = window.prompt(
      `Cantidad total preparada para ${item.product.code} / ${item.color} / ${item.size}\nStock teórico: ${cell.stockQty}\nPendiente: ${item.pending_quantity}`,
      String(defaultValue)
    );
    if (response === null) return;
    handleQuantityChange(item.id, response);
  };

  const handlePrepareGroupByStock = (group) => {
    const nextQuantities = { ...quantities };
    const nextTouched = { ...quantityTouched };
    let changedCount = 0;

    group.items.forEach(item => {
      const currentPrepared = item.preparation_quantity || 0;
      const remainingToPrepare = Math.max(0, (item.pending_quantity || 0) - currentPrepared);
      const stockQty = stockAvailable[item.id] || 0;
      if (remainingToPrepare > 0 && stockQty > 0) {
        nextQuantities[item.id] = Math.min(item.pending_quantity || 0, currentPrepared + Math.min(remainingToPrepare, stockQty));
        nextTouched[item.id] = true;
        changedCount += 1;
      }
    });

    setQuantities(nextQuantities);
    setQuantityTouched(nextTouched);

    toast({
      title: changedCount > 0 ? 'Línea marcada' : 'Sin cambios',
      description: changedCount > 0
        ? 'Las líneas con stock quedan marcadas. Pulsa Preparar Pedido para guardar.'
        : 'No hay líneas pendientes con stock teórico en este grupo.',
    });
  };

  // Calcular totales para el resumen
  const calculateTotals = () => {
    if (!order) return { totalPending: 0, totalToPrepare: 0, totalPrepared: 0 };
    
    let totalPending = 0;
    let totalToPrepare = 0;
    let totalPrepared = 0;
    
    order.order_items.forEach(item => {
      // Cantidad realmente pendiente (pendiente - ya preparado)
      const realPendingQuantity = Math.max(0, item.pending_quantity - item.preparation_quantity);
      totalPending += realPendingQuantity;
      
      // Cantidad ya preparada
      totalPrepared += item.preparation_quantity || 0;
      
      // Cantidad marcada en esta preparación antes de guardar
      totalToPrepare += quantityTouched[item.id]
        ? Math.max(0, (quantities[item.id] || 0) - (item.preparation_quantity || 0))
        : 0;
    });
    
    return { totalPending, totalToPrepare, totalPrepared };
  };

  const handlePrint = () => {
    const printGroups = groupItemsByProduct('color');
    const printSizes = getVisibleSizes(printGroups);
    const compactPrintWindow = window.open('', '_blank');
    const compactRows = printGroups.map(group => {
      const cells = printSizes.map(size => {
        const item = group.items.find(groupItem => groupItem.size === size);
        if (!item) return '<td class="empty">-</td>';
        const stockQty = stockAvailable[item.id] || 0;
        const remaining = Math.max(0, (item.pending_quantity || 0) - (item.preparation_quantity || 0));
        if (remaining <= 0) return '<td class="empty">-</td>';
        const tone = stockQty >= remaining ? 'stock-ok' : 'stock-missing';
        return `<td class="${tone}">${remaining}</td>`;
      }).join('');
      const total = group.items.reduce((sum, item) => sum + Math.max(0, (item.pending_quantity || 0) - (item.preparation_quantity || 0)), 0);
      return `
        <tr>
          <td class="image-cell">
            ${group.productImage
              ? `<img src="${group.productImage}" alt="${group.productDescription || group.productCode}" class="product-image">`
              : `<div class="product-image placeholder">Sin imagen</div>`
            }
          </td>
          <td class="article">
            <div class="code">${group.productCode}</div>
            <div class="description">${group.productDescription || ''}</div>
          </td>
          <td>${getColorDescription(group.color)}</td>
          ${cells}
          <td class="total">${total}</td>
          <td class="manual"></td>
        </tr>
      `;
    }).join('');

    const compactPrintContent = `
      <html>
        <head>
          <title>Preparación de Pedido #${order.number}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; margin: 18px; color: #111827; }
            h1 { margin: 0 0 8px; font-size: 22px; }
            .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 14px; font-size: 12px; }
            .meta div { border: 1px solid #d1d5db; padding: 6px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #d1d5db; padding: 6px; text-align: center; }
            th { background: #f3f4f6; }
            .image-cell { width: 72px; min-width: 72px; }
            .product-image {
              width: 58px;
              height: 58px;
              object-fit: contain;
              border: 1px solid #d1d5db;
              background: #ffffff;
              display: block;
              margin: 0 auto;
            }
            .product-image.placeholder {
              display: flex;
              align-items: center;
              justify-content: center;
              background: #f3f4f6;
              color: #6b7280;
              font-size: 10px;
              line-height: 1.1;
            }
            .article { min-width: 180px; text-align: left; }
            .code { font-weight: 700; font-size: 13px; }
            .description { color: #4b5563; font-size: 11px; }
            .stock-ok { background: #dbeafe; font-weight: 700; }
            .stock-missing { background: #fee2e2; font-weight: 700; }
            .empty { color: #9ca3af; }
            .total { font-weight: 700; }
            .manual { min-width: 90px; }
            .legend { margin-top: 10px; font-size: 11px; color: #374151; }
            @media print {
              @page { size: landscape; margin: 10mm; }
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Preparación de Pedido #${order.number}</h1>
          <div class="meta">
            <div><strong>Cliente</strong><br>${order.customer?.name || 'N/A'}</div>
            <div><strong>Fecha pedido</strong><br>${new Date(order.date).toLocaleDateString('es-ES')}</div>
            <div><strong>Impresión</strong><br>${new Date().toLocaleString('es-ES')}</div>
            <div><strong>Pendientes</strong><br>${totals.totalPending}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Imagen</th>
                <th>Artículo</th>
                <th>Color</th>
                ${printSizes.map(size => `<th>${size}</th>`).join('')}
                <th>Total</th>
                <th>Manual</th>
              </tr>
            </thead>
            <tbody>${compactRows}</tbody>
          </table>
          <div class="legend">Azul: stock teórico suficiente. Rojo: requiere regularizar stock antes de servir. La hoja muestra solo pendientes reales.</div>
          <script>window.onload = function() { window.print(); };</script>
        </body>
      </html>
    `;

    compactPrintWindow.document.write(compactPrintContent);
    compactPrintWindow.document.close();
    return;
    
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>Preparación de Pedido #${order.number}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
            
            * { box-sizing: border-box; }
            
            body { 
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: #f8fafc;
              color: #1e293b;
              line-height: 1.6;
            }
            
            .print-container {
              max-width: 1200px;
              margin: 0 auto;
              background: white;
              border-radius: 12px;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
              overflow: hidden;
            }
            
            .header {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 30px;
              text-align: center;
            }
            
            .header h1 {
              margin: 0 0 10px 0;
              font-size: 2.5rem;
              font-weight: 700;
              letter-spacing: -0.025em;
            }
            
            .header-info {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 20px;
              margin-top: 20px;
            }
            
            .header-item {
              background: rgba(255, 255, 255, 0.1);
              padding: 15px;
              border-radius: 8px;
              backdrop-filter: blur(10px);
            }
            
            .header-item h3 {
              margin: 0 0 5px 0;
              font-size: 0.875rem;
              font-weight: 500;
              opacity: 0.9;
              text-transform: uppercase;
              letter-spacing: 0.05em;
            }
            
            .header-item p {
              margin: 0;
              font-size: 1.125rem;
              font-weight: 600;
            }
            
            .content {
              padding: 30px;
            }
            
            .product-card {
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              margin-bottom: 30px;
              overflow: hidden;
              box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
            }
            
            .product-header {
              background: #f8fafc;
              padding: 25px;
              border-bottom: 1px solid #e2e8f0;
              display: flex;
              align-items: center;
              gap: 25px;
            }
            
            .product-image {
              width: 120px;
              height: 120px;
              object-fit: cover;
              border-radius: 8px;
              border: 2px solid #e2e8f0;
            }
            
            .product-info {
              flex: 1;
            }
            
            .product-code {
              font-size: 2rem;
              font-weight: 700;
              color: #1e293b;
              margin: 0 0 10px 0;
              letter-spacing: -0.025em;
            }
            
            .product-description {
              font-size: 1.25rem;
              color: #475569;
              margin: 0 0 15px 0;
              font-weight: 500;
            }
            
            .product-color {
              display: flex;
              align-items: center;
              gap: 10px;
            }
            
            .color-swatch {
              width: 24px;
              height: 24px;
              border-radius: 50%;
              border: 2px solid #e2e8f0;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              display: inline-block;
              vertical-align: middle;
            }
            
            .color-name {
              font-size: 1rem;
              color: #64748b;
              font-weight: 500;
            }
            
            .table-container {
              overflow-x: auto;
              margin: 0;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 0;
            }
            
            th {
              background: #f1f5f9;
              color: #475569;
              font-weight: 600;
              text-transform: uppercase;
              font-size: 0.75rem;
              letter-spacing: 0.05em;
              padding: 15px 12px;
              text-align: left;
              border-bottom: 2px solid #e2e8f0;
            }
            
            td {
              padding: 15px 12px;
              border-bottom: 1px solid #f1f5f9;
              vertical-align: middle;
            }
            
            .status-badge {
              display: inline-flex;
              align-items: center;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 0.75rem;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.05em;
            }
            
            .status-pending {
              background: #fef3c7;
              color: #92400e;
            }
            
            .status-prepared {
              background: #d1fae5;
              color: #065f46;
            }
            
            .quantity-input {
              width: 60px;
              height: 40px;
              border: 2px solid #d1d5db;
              border-radius: 6px;
              text-align: center;
              font-size: 1rem;
              font-weight: 600;
              color: #374151;
              background: #f9fafb;
            }
            
            .notes-cell {
              max-width: 200px;
            }
            
            .notes-text {
              font-size: 0.875rem;
              color: #6b7280;
              font-style: italic;
            }
            
            .footer {
              background: #f8fafc;
              padding: 25px;
              border-top: 1px solid #e2e8f0;
              text-align: center;
            }
            
            .print-button {
              background: #3b82f6;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
            }
            
            .print-button:hover {
              background: #2563eb;
              transform: translateY(-1px);
            }
            
            @media print {
              body { background: white; }
              .print-container { box-shadow: none; }
              .print-button { display: none; }
              .product-card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="header">
              <h1>Preparación de Pedido #${order.number}</h1>
              <div class="header-info">
                <div class="header-item">
                  <h3>Cliente</h3>
                  <p>${order.customer?.name || 'N/A'}</p>
                </div>
                <div class="header-item">
                  <h3>Fecha del Pedido</h3>
                  <p>${new Date(order.date).toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</p>
                </div>
                <div class="header-item">
                  <h3>Total del Pedido</h3>
                  <p>€${order.total?.toFixed(2) || '0.00'}</p>
                </div>
                <div class="header-item">
                  <h3>Fecha de Impresión</h3>
                  <p>${new Date().toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</p>
                </div>
              </div>
            </div>
            
            <div class="content">
              ${groupItemsByProduct().map(group => `
                <div class="product-card">
                  <div class="product-header">
                    ${group.productImage ? 
                      `<img src="${group.productImage}" alt="${group.productDescription}" class="product-image">` : 
                      `<div class="product-image" style="background: #e2e8f0; display: flex; align-items: center; justify-content: center; color: #64748b; font-size: 0.875rem; text-align: center;">Sin imagen</div>`
                    }
                    <div class="product-info">
                      <h2 class="product-code">${group.productCode}</h2>
                      <p class="product-description">${group.productDescription}</p>
                      <div class="product-color">
                        <div class="color-swatch" style="background-color: ${getColorHex(group.color)}; border: 2px solid #e2e8f0;" title="Color: ${getColorDescription(group.color)} (${getColorHex(group.color)})"></div>
                        <span class="color-name">${getColorDescription(group.color)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Estado</th>
                          <th>Talla</th>
                          <th>Stock</th>
                          <th>Pendiente</th>
                          <th>Ya Preparado</th>
                          <th>A Preparar</th>
                          <th>Notas</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${group.items.map(item => {
                          const stockQty = stockAvailable[item.id] || 0;
                          const isCompleted = (item.preparation_quantity || 0) >= item.pending_quantity;
                          const hasPreparation = (item.preparation_quantity || 0) > 0;
                          
                          return `
                            <tr>
                              <td>
                                <span class="status-badge ${isCompleted ? 'status-prepared' : 'status-pending'}">
                                  ${isCompleted ? 'Completado' : 'Pendiente'}
                                </span>
                              </td>
                              <td style="font-weight: 600; font-size: 1.1rem;">${item.size}</td>
                              <td style="font-weight: 600; color: ${stockQty > 0 ? '#059669' : '#dc2626'};">
                                ${stockQty}
                              </td>
                              <td style="font-weight: 600;">${item.pending_quantity}</td>
                              <td style="font-weight: 600; color: #059669;">${item.preparation_quantity || 0}</td>
                              <td>
                                <input type="number" class="quantity-input" value="${quantities[item.id] || 0}" readonly>
                              </td>
                              <td class="notes-cell">
                                <span class="notes-text">${notes[item.id] || ''}</span>
                              </td>
                            </tr>
                          `;
                        }).join('')}
                      </tbody>
                    </table>
                  </div>
                </div>
              `).join('')}
            </div>
            
            <div class="footer">
              <button class="print-button" onclick="window.print()">
                🖨️ Imprimir Preparación
              </button>
            </div>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Función auxiliar para obtener el color hexadecimal usando el contexto
  const getColorHex = (colorName) => {
    if (!colorName) return '#C0C0C0';
    
    // Primero intentar usar el contexto de colores
    if (colorSettings && colorSettings[colorName]) {
      const colorData = colorSettings[colorName];
      const hexColor = typeof colorData === 'string' ? colorData : colorData.hex;
      
      return hexColor || '#C0C0C0';
    }
    
    // Fallback: mapeo manual de colores comunes
    const colorMap = {
      'negro': '#000000',
      'blanco': '#FFFFFF',
      'rojo': '#FF0000',
      'azul': '#0000FF',
      'verde': '#008000',
      'amarillo': '#FFFF00',
      'gris': '#808080',
      'marron': '#8B4513',
      'rosa': '#FFC0CB',
      'morado': '#800080',
      'naranja': '#FFA500',
      'kaki': '#C3B091',
      'beige': '#F5F5DC',
      'crema': '#FFFDD0',
      'azul marino': '#000080',
      'verde oliva': '#808000',
      'rojo oscuro': '#8B0000',
      'azul claro': '#87CEEB',
      'verde claro': '#90EE90',
      'gris claro': '#D3D3D3',
      'gris oscuro': '#404040',
      // Variaciones comunes
      'caqui': '#C3B091',
      'khaki': '#C3B091',
      'café': '#8B4513',
      'cafe': '#8B4513',
      'brown': '#8B4513',
      'black': '#000000',
      'white': '#FFFFFF',
      'red': '#FF0000',
      'blue': '#0000FF',
      'green': '#008000',
      'yellow': '#FFFF00',
      'gray': '#808080',
      'grey': '#808080',
      'pink': '#FFC0CB',
      'purple': '#800080',
      'orange': '#FFA500'
    };
    
    const normalizedColor = colorName.toLowerCase().trim();
    const hexColor = colorMap[normalizedColor];
    

    
    return hexColor || '#C0C0C0';
  };

  // Función auxiliar para obtener la descripción del color
  const getColorDescription = (colorName) => {
    if (!colorName) return 'Sin color';
    
    // Usar el contexto de colores si está disponible
    if (colorSettings && colorSettings[colorName]) {
      const colorData = colorSettings[colorName];
      const description = typeof colorData === 'string' ? colorName : (colorData.description || colorName);
      return description;
    }
    
    // Fallback: usar el nombre del color tal como viene
    return colorName;
  };

  const closePrintAfterSaveDialog = () => {
    setShowPrintAfterSaveDialog(false);
    onClose();
  };

  const handlePrintPreparedDocument = async () => {
    const printed = await printPreparedOrderDocument({
      orderId,
      toast,
      onPrintingChange: setIsPreparedDocumentPrinting,
    });

    if (printed) {
      closePrintAfterSaveDialog();
    }
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  const groupedItems = groupItemsByProduct(mobilePicking ? 'size' : 'color');
  const visibleSizes = mobilePicking ? [] : getVisibleSizes(groupedItems);
  const hasStockIssues = order.order_items.some(item => {
    const stockQty = stockAvailable[item.id] || 0;
    return stockQty < item.pending_quantity;
  });

  const totals = calculateTotals();
  const canServePrepared = order?.order_items?.some((item) =>
    Math.min(item.preparation_quantity || 0, item.pending_quantity || 0) > 0
  );

  return (
    <div className={`${mobilePicking ? 'mobile-picking-form space-y-2 h-[100dvh] max-h-[100dvh] min-h-0 overflow-hidden p-2 text-sm' : 'space-y-4 max-h-[85vh] p-4'} flex flex-col bg-white w-full`}>
      {/* Cabecera rediseñada (sticky) */}
      <div className={`${mobilePicking ? 'mobile-picking-header gap-1 pb-1 mb-1 shrink-0' : 'gap-1 pb-2 mb-2'} flex flex-col border-b sticky top-0 bg-white z-20`}>
        {/* Fila 1: Título, total y acciones */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <h1 className={`${mobilePicking ? 'text-base' : 'text-lg'} font-semibold mb-0.5`}>
              {mobilePicking ? `Preparar pedido #${order.number || ''}` : 'Preparar Pedido'}
            </h1>
            {mobilePicking && (
              <div className="mobile-picking-title-meta truncate text-xs text-gray-500">
                {order.customer?.name} · {new Date(order.date).toLocaleDateString()}
              </div>
            )}
          </div>
          <div className="mobile-picking-header-actions flex flex-wrap items-center justify-end gap-2">
            <div className={`text-right ${mobilePicking ? 'hidden sm:block' : ''}`}>
              <div className="text-xs text-gray-500 font-medium">Total</div>
              <div className="text-xl font-bold text-gray-800">${order.total?.toFixed(2)}</div>
            </div>
            <Button 
              variant="outline" 
              onClick={onClose}
              className={mobilePicking ? 'mobile-picking-secondary' : undefined}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handlePrepare}
              disabled={preparing || !Object.values(quantityTouched).some(Boolean)}
              className="bg-gray-600 hover:bg-gray-700 text-white"
            >
              {preparing ? 'Preparando...' : mobilePicking ? 'Guardar' : 'Preparar Pedido'}
            </Button>
            {onServe && (
              <Button
                onClick={() => onServe(orderId)}
                disabled={preparing || !canServePrepared}
                className="bg-green-600 hover:bg-green-700 text-white"
                title={canServePrepared ? 'Servir las prendas ya preparadas' : 'No hay prendas preparadas pendientes de servir'}
              >
                <Truck className="h-4 w-4 mr-2" />
                {mobilePicking ? 'Servir' : 'Servir Pedido'}
              </Button>
            )}
            {showMobileQr && !mobilePicking && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowQrDialog(true)}
                className="flex items-center gap-2"
                title="Abrir QR para preparar este pedido desde el móvil"
              >
                <QrCode className="h-4 w-4" />
                QR móvil
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className={`flex items-center gap-2 ${mobilePicking ? 'mobile-picking-secondary' : ''}`}
            >
              <Printer className="h-4 w-4" />
              {mobilePicking ? 'Imprimir' : 'Imprimir'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSummary(prev => !prev)}
              className={`h-8 px-2 ${mobilePicking ? 'mobile-picking-secondary' : ''}`}
              title={showSummary ? 'Ocultar resumen' : 'Mostrar resumen'}
            >
              {showSummary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        {showSummary && (
          <>
            {/* Fila 2: Datos cliente y resumen totales */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mt-1">
              {/* Datos cliente */}
              <div className="flex flex-col gap-0.5 min-w-[160px]">
                <div className="text-xs text-gray-500 font-medium">Cliente</div>
                <div className="text-sm font-semibold text-gray-800 leading-tight">{order.customer?.name}</div>
                <div className="text-[11px] text-gray-500">Fecha: {new Date(order.date).toLocaleDateString()}</div>
              </div>
              {/* Totales */}
              <div className={`flex-1 flex flex-row justify-center ${mobilePicking ? 'gap-2' : 'gap-4'} mt-1 md:mt-0`}>
                <div className={`bg-white shadow rounded-md ${mobilePicking ? 'px-2 py-1 min-w-[70px]' : 'px-4 py-2 min-w-[90px]'} flex flex-col items-center border border-orange-100`}>
                  <div className={`${mobilePicking ? 'text-lg' : 'text-2xl'} font-extrabold text-orange-500`}>{totals.totalPending}</div>
                  <div className="uppercase text-[10px] text-gray-400 font-semibold tracking-wider mt-0.5">Pendientes</div>
                </div>
                <div className={`bg-white shadow rounded-md ${mobilePicking ? 'px-2 py-1 min-w-[70px]' : 'px-4 py-2 min-w-[90px]'} flex flex-col items-center border border-blue-100`}>
                  <div className={`${mobilePicking ? 'text-lg' : 'text-2xl'} font-extrabold text-blue-500`}>{totals.totalToPrepare}</div>
                  <div className="uppercase text-[10px] text-gray-400 font-semibold tracking-wider mt-0.5">A preparar</div>
                </div>
                <div className={`bg-white shadow rounded-md ${mobilePicking ? 'px-2 py-1 min-w-[70px]' : 'px-4 py-2 min-w-[90px]'} flex flex-col items-center border border-green-100`}>
                  <div className={`${mobilePicking ? 'text-lg' : 'text-2xl'} font-extrabold text-green-500`}>{totals.totalPrepared}</div>
                  <div className="uppercase text-[10px] text-gray-400 font-semibold tracking-wider mt-0.5">Ya preparadas</div>
                </div>
              </div>
            </div>
            {/* Barra de progreso */}
            <div className="w-full max-w-xl mx-auto mt-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2 relative overflow-hidden">
                  {(() => {
                    const totalItems = totals.totalPending + totals.totalPrepared;
                    const progressPercentage = totalItems > 0 ? (totals.totalPrepared / totalItems) * 100 : 0;
                    return (
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(100, progressPercentage)}%` }}
                      />
                    );
                  })()}
                </div>
                <div className="w-10 text-center text-xs font-semibold text-gray-700">
                  {(() => {
                    const totalItems = totals.totalPending + totals.totalPrepared;
                    const progressPercentage = totalItems > 0 ? (totals.totalPrepared / totalItems) * 100 : 0;
                    return `${Math.round(progressPercentage)}%`;
                  })()}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {error && (
        <Alert variant="destructive" className={mobilePicking ? 'mobile-picking-alert' : undefined}>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {hasStockIssues && (
        <Alert variant="warning" className={`bg-yellow-50 border-yellow-200 ${mobilePicking ? 'mobile-picking-alert' : ''}`}>
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-600">Atención</AlertTitle>
          <AlertDescription className="text-yellow-700">
            Algunas líneas tienen stock insuficiente para la cantidad pendiente
          </AlertDescription>
        </Alert>
      )}

      <div className={`${mobilePicking ? 'mobile-picking-toolbar flex flex-wrap items-center gap-2 mb-1 shrink-0' : 'flex justify-between items-center mb-4'}`}>
        <div className={`flex items-center gap-2 ${mobilePicking ? '' : 'min-w-[220px]'}`}>
          <Switch
            id="show-prepared"
            checked={showOnlyPrepared}
            onCheckedChange={setShowOnlyPrepared}
          />
          <Label htmlFor="show-prepared" className={`${mobilePicking ? 'text-xs' : 'text-sm'}`}>
            <div className="flex items-center gap-1">
              <Check className="h-4 w-4" />
              {mobilePicking ? 'Preparadas' : 'Solo ya preparadas'}
            </div>
          </Label>
        </div>
        <div className={`${mobilePicking ? 'mobile-picking-filter min-w-[180px] flex-1' : 'flex-1 mx-4 max-w-[540px]'}`}>
          <div className="relative">
            <Input
              placeholder={mobilePicking ? 'Filtrar artículo...' : 'Filtrar por código de artículo...'}
              value={codeFilter}
              onChange={(e) => setCodeFilter(e.target.value)}
              className={`${mobilePicking ? 'h-8' : ''} w-full pr-8`}
            />
            {codeFilter && (
              <button
                type="button"
                onClick={() => setCodeFilter('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Limpiar filtro"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={handlePrepareAll}
            variant="outline"
            size={mobilePicking ? 'sm' : 'default'}
            className="flex items-center gap-2"
          >
            <Check className="h-4 w-4" />
            {mobilePicking ? 'Todo stock' : 'Preparar Todo según Stock'}
          </Button>
          <Button
            onClick={handleResetInputs}
            variant="ghost"
            size={mobilePicking ? 'sm' : 'default'}
            className="flex items-center gap-2"
            disabled={preparing}
            title="Reinicia los campos 'A Preparar'"
          >
            <RotateCcw className="h-4 w-4" />
            {mobilePicking ? 'Reset' : 'Reset A Preparar'}
          </Button>
          <Button
            onClick={handleResetPreparedAll}
            variant="outline"
            size={mobilePicking ? 'sm' : 'default'}
            className="flex items-center gap-2 text-red-600 border-red-200 hover:text-red-700"
            disabled={preparing}
            title="Deshace toda la preparación ya guardada"
          >
            <RotateCcw className="h-4 w-4" />
            {mobilePicking ? 'Reset prep.' : 'Reset Ya Preparado'}
          </Button>
        </div>
      </div>

      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Preparación móvil</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            <div className="rounded-lg border bg-white p-4">
              <QRCodeSVG value={mobilePickingUrl} size={220} level="M" includeMargin />
            </div>
            <div className="w-full rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600 break-all">
              {mobilePickingUrl}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPrintAfterSaveDialog} onOpenChange={(open) => {
        if (!open && !isPreparedDocumentPrinting) {
          closePrintAfterSaveDialog();
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Preparación guardada</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              ¿Quieres imprimir el documento de preparación para asociarlo a la caja?
            </p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={closePrintAfterSaveDialog}
                disabled={isPreparedDocumentPrinting}
              >
                No imprimir
              </Button>
              <Button
                type="button"
                onClick={handlePrintPreparedDocument}
                disabled={isPreparedDocumentPrinting}
                className="bg-gray-700 text-white hover:bg-gray-800"
              >
                <Printer className="mr-2 h-4 w-4" />
                {isPreparedDocumentPrinting ? 'Preparando...' : 'Imprimir'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className={`${mobilePicking ? 'mobile-picking-detail min-h-0 space-y-2 overflow-auto pb-2' : 'overflow-auto'} flex-1`}>
        {groupedItems.length === 0 && (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
            No hay líneas que coincidan con el filtro actual.
          </div>
        )}

        {mobilePicking && groupedItems.length > 0 && (
          <div className="space-y-2">
            {groupedItems.map((group) => {
              const groupPending = group.items.reduce((sum, item) => sum + getCellData(item).remainingToPrepare, 0);
              const groupPreparedDraft = group.items.reduce((sum, item) => {
                const cell = getCellData(item);
                return sum + Math.max(0, cell.effectivePreparedQty - cell.savedPreparedQty);
              }, 0);
              const groupSavedPending = group.items.reduce(
                (sum, item) => sum + Math.max(0, (item.pending_quantity || 0) - (item.preparation_quantity || 0)),
                0
              );
              const groupComplete = groupPending === 0;

              return (
                <div
                  key={group.key}
                  className={`mobile-picking-card rounded-md border p-2 shadow-sm ${groupComplete ? 'border-green-200 bg-green-50/70' : 'border-gray-200 bg-white'}`}
                >
                  <div className="mobile-picking-card-header mb-2 flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      {group.productImage && (
                        <img
                          src={group.productImage}
                          alt={group.productCode}
                          className="mobile-picking-product-img h-12 w-12 shrink-0 rounded object-cover"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="text-base font-bold text-gray-900">{group.productCode}</span>
                          <span className="text-sm font-semibold text-gray-700">Talla {group.size}</span>
                        </div>
                        <div className="line-clamp-1 text-xs text-gray-500">{group.productDescription}</div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <div className="rounded border bg-white px-2 py-1 text-center">
                        <div className="text-[10px] uppercase text-gray-400">Pend.</div>
                        <div className="text-sm font-bold text-gray-900">{groupPending}</div>
                      </div>
                      {groupPreparedDraft > 0 && (
                        <div className="rounded border border-green-200 bg-green-50 px-2 py-1 text-center">
                          <div className="text-[10px] uppercase text-green-600">Marc.</div>
                          <div className="text-sm font-bold text-green-700">+{groupPreparedDraft}</div>
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-9 px-2"
                        onClick={() => handlePrepareGroupByStock(group)}
                        disabled={preparing || groupSavedPending === 0}
                        title="Marca esta talla según stock teórico sin guardar todavía"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mobile-picking-size-grid grid grid-cols-[repeat(auto-fill,minmax(76px,1fr))] gap-1.5">
                    {group.items.map((item) => {
                      const cell = getCellData(item);
                      const colorLabel = getColorDescription(item.color);
                      const title = `Color: ${colorLabel} · Talla: ${item.size} · Stock: ${cell.stockQty} · Pendiente: ${item.pending_quantity} · Preparado: ${cell.effectivePreparedQty}`;

                      return (
                        <button
                          key={item.id}
                          type="button"
                          className={`mobile-picking-size-button relative flex h-16 flex-col items-center justify-center rounded border px-1 transition-colors ${getCellClassName(cell.state)}`}
                          onClick={() => handleCellClick(item)}
                          onDoubleClick={() => openQuantityEditor(item)}
                          onContextMenu={(event) => {
                            event.preventDefault();
                            openQuantityEditor(item);
                          }}
                          title={`${title}. Click valida, doble click o pulsación larga edita.`}
                          disabled={(item.pending_quantity || 0) === 0 && !cell.effectivePreparedQty}
                        >
                          <span className="flex max-w-full items-center justify-center gap-1 text-[10px] font-semibold leading-none">
                            <span
                              className="h-3 w-3 shrink-0 rounded-full border border-gray-300"
                              style={{ backgroundColor: getColorHex(item.color) }}
                            />
                            <span className="truncate">{colorLabel}</span>
                          </span>
                          <span className="text-xl font-extrabold leading-tight">
                            {cell.displayQty > 0 ? cell.displayQty : "-"}
                          </span>
                          <span className="text-[10px] leading-none opacity-75">S {cell.stockQty}</span>
                          {cell.hasDraft && (
                            <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-gray-900" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!mobilePicking && groupedItems.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-10 min-w-[260px] bg-white">Artículo / color</TableHead>
                {visibleSizes.map(size => (
                  <TableHead key={size} className="min-w-[72px] text-center">{size}</TableHead>
                ))}
                <TableHead className="min-w-[90px] text-center">Total</TableHead>
                <TableHead className="min-w-[140px] text-right">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupedItems.map((group) => {
                const groupPending = group.items.reduce((sum, item) => sum + getCellData(item).remainingToPrepare, 0);
                const groupPreparedDraft = group.items.reduce((sum, item) => {
                  const cell = getCellData(item);
                  return sum + Math.max(0, cell.effectivePreparedQty - cell.savedPreparedQty);
                }, 0);
                const groupComplete = groupPending === 0;
                const groupSavedPending = group.items.reduce(
                  (sum, item) => sum + Math.max(0, (item.pending_quantity || 0) - (item.preparation_quantity || 0)),
                  0
                );

                return (
                  <TableRow
                    key={`${group.productCode}-${group.color}`}
                    className={groupComplete ? "bg-green-50/60" : undefined}
                  >
                    <TableCell className="sticky left-0 z-10 bg-inherit align-middle">
                      <div className="flex items-center gap-3">
                        {group.productImage && (
                          <img
                            src={group.productImage}
                            alt={group.productCode}
                            className="h-12 w-12 rounded object-cover"
                          />
                        )}
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-900">{group.productCode}</span>
                            <ColorBadge color={group.color} />
                          </div>
                          <div className="truncate text-xs text-gray-500">{group.productDescription}</div>
                        </div>
                      </div>
                    </TableCell>

                    {visibleSizes.map(size => {
                      const item = group.items.find(groupItem => groupItem.size === size);
                      if (!item) {
                        return (
                          <TableCell key={`${group.productCode}-${group.color}-${size}`} className="text-center">
                            <div className="mx-auto h-10 w-14 rounded border border-gray-100 bg-gray-50" />
                          </TableCell>
                        );
                      }

                      const cell = getCellData(item);
                      const title = `Stock: ${cell.stockQty} · Pendiente: ${item.pending_quantity} · Preparado: ${cell.effectivePreparedQty}`;

                      return (
                        <TableCell key={item.id} className="text-center">
                          <button
                            type="button"
                            className={`relative mx-auto flex h-10 w-14 items-center justify-center rounded border text-lg font-bold transition-colors ${getCellClassName(cell.state)}`}
                            onClick={() => handleCellClick(item)}
                            onDoubleClick={() => openQuantityEditor(item)}
                            onContextMenu={(event) => {
                              event.preventDefault();
                              openQuantityEditor(item);
                            }}
                            title={`${title}. Click valida, doble click o click derecho edita.`}
                            disabled={(item.pending_quantity || 0) === 0 && !cell.effectivePreparedQty}
                          >
                            {cell.displayQty > 0 ? cell.displayQty : "-"}
                            {cell.hasDraft && (
                              <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-gray-900" />
                            )}
                          </button>
                        </TableCell>
                      );
                    })}

                    <TableCell className="text-center">
                      <div className="text-sm font-semibold text-gray-900">{groupPending}</div>
                      {groupPreparedDraft > 0 && (
                        <div className="text-[11px] text-green-700">+{groupPreparedDraft} marcado</div>
                      )}
                    </TableCell>

                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePrepareGroupByStock(group)}
                          disabled={preparing || groupSavedPending === 0}
                          title="Marca esta línea según stock teórico sin guardar todavía"
                        >
                          <Check className="mr-1 h-4 w-4" />
                          Línea
                        </Button>
                        {group.items.some(item => (item.preparation_quantity || 0) > 0) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-yellow-600 hover:text-yellow-700"
                            onClick={() => {
                              const preparedItem = group.items.find(item => (item.preparation_quantity || 0) > 0);
                              if (preparedItem) handleUnprepare(preparedItem.id);
                            }}
                            title="Despreparar una talla ya preparada de la línea"
                            disabled={preparing}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Botonera inferior eliminada para ganar espacio vertical; acciones están en cabecera */}
    </div>
  );
} 
